"use client";

import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

interface ExpandCardProps {
    /**
     * Header content, usually a title. displayed on the left.
     */
    title: React.ReactNode;
    /**
     * The content to be expanded/collapsed.
     */
    children: React.ReactNode;
    /**
     * Whether the card is initially expanded.
     * @default false
     */
    defaultExpanded?: boolean;
    /**
     * Class name for the outer container.
     */
    className?: string;
    /**
     * Class name for the header container.
     */
    headerClassName?: string;
    /**
     * Class name for the content container inner wrapper.
     */
    contentClassName?: string;
    onExpandChange?: ( expanded: boolean ) => void;
    /**
     * Optional actions rendered to the left of the expand/collapse chevron (e.g. refresh button).
     * Clicks are stopped from toggling the card.
     */
    rightActions?: React.ReactNode;
}

export function ExpandCard(
    {
        title,
        children,
        defaultExpanded = false,
        className,
        headerClassName,
        contentClassName,
        onExpandChange,
        rightActions,
    }: ExpandCardProps,
) {
    const [ isExpanded, setIsExpanded ] = useState( defaultExpanded );
    useEffect( () => {
        onExpandChange?.( !isExpanded );
    }, [ isExpanded ] );

    return (
        <div className={cn( "w-full", className )}>
            {/* Header Section */}
            <div
                className={cn( "flex items-center justify-between cursor-pointer select-none", headerClassName )}
                onClick={() => {
                    setIsExpanded( !isExpanded );
                }}
            >
                <div className="flex-1 text-t1 text-base font-semibold">{title}</div>
                <div className="flex items-center justify-center text-t3 ml-2">
                    {rightActions != null && (
                        <div
                            className="flex items-center mr-2"
                            onClick={( e ) => e.stopPropagation()}
                        >
                            {rightActions}
                        </div>
                    )}
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        className={cn( "transition-transform duration-200 size-6", isExpanded ? "" : "rotate-180" )}
                    >
                        <path
                            d="M16.59 15.4102L12 10.8302L7.41 15.4102L6 14.0002L12 8.00016L18 14.0002L16.59 15.4102Z"
                            fill="var(--ui-content-t3)"
                        />
                    </svg>
                </div>
            </div>

            {/* Content Section */}
            <AnimatePresence initial={false}>
                {isExpanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                    >
                        <div className={cn( contentClassName )}>{children}</div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
